import { useEffect, useRef, useState, useCallback } from "react";
import { Camera, MapPin, CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Selfie + geolocation attendance capture.
 * First call of the day → checks in. Second call → checks out.
 * Requires user to grant CAMERA and LOCATION permissions.
 */
export default function SelfieCheckIn({ onDone }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [streaming, setStreaming] = useState(false);
  const [camError, setCamError] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [coords, setCoords] = useState(null);
  const [locError, setLocError] = useState("");
  const [loading, setLoading] = useState(false);

  const startCam = useCallback(async () => {
    setCamError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setStreaming(true);
    } catch (e) {
      setCamError("Camera access denied. Grant camera permission and reload.");
      setStreaming(false);
    }
  }, []);

  const stopCam = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStreaming(false);
  }, []);

  const getGeo = useCallback(() => {
    setLocError("");
    if (!("geolocation" in navigator)) {
      setLocError("Geolocation is not supported by your browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => setCoords({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      (e) => setLocError(e.message || "Location permission denied."),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  }, []);

  useEffect(() => {
    startCam();
    getGeo();
    return () => stopCam();
  }, [startCam, getGeo, stopCam]);

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) {
      toast.error("Camera not ready yet");
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1); // mirror horizontally for a natural selfie
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.restore();
    const url = canvas.toDataURL("image/jpeg", 0.7);
    setSnapshot(url);
    stopCam();
  };

  const retake = async () => {
    setSnapshot(null);
    await startCam();
  };

  const submit = async () => {
    if (!snapshot) return toast.error("Please take a selfie first");
    if (!coords) return toast.error("Location required — grant location permission");
    setLoading(true);
    try {
      const { data } = await api.post("/attendance/selfie", {
        lat: coords.lat,
        lng: coords.lng,
        accuracy: coords.accuracy,
        selfie_data_url: snapshot,
      });
      toast.success(data.action === "check_in" ? "Checked in with selfie ✓" : "Checked out with selfie ✓");
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to mark attendance"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="selfie-widget">
      <div className="relative border border-neutral-950 bg-neutral-950 aspect-video overflow-hidden">
        {!snapshot ? (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ transform: "scaleX(-1)" }}
              className="w-full h-full object-cover"
              data-testid="selfie-video"
            />
            {!streaming && (
              <div className="absolute inset-0 flex items-center justify-center text-[#D4B87D] font-mono text-sm">
                {camError || "Starting camera…"}
              </div>
            )}
          </>
        ) : (
          <img src={snapshot} alt="Captured selfie" className="w-full h-full object-cover" data-testid="selfie-snapshot" />
        )}
        <canvas ref={canvasRef} className="hidden" />

        {/* Overlay: location badge */}
        <div className="absolute top-3 left-3 flex flex-col gap-1">
          {coords ? (
            <span className="px-2 py-1 bg-black/70 text-[#D4B87D] font-mono text-[10px] flex items-center gap-1" data-testid="geo-badge">
              <MapPin className="w-3 h-3" /> {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)} · ±{Math.round(coords.accuracy)}m
            </span>
          ) : locError ? (
            <span className="px-2 py-1 bg-black/70 text-[#FF3B30] font-mono text-[10px] flex items-center gap-1">
              <XCircle className="w-3 h-3" /> {locError}
            </span>
          ) : (
            <span className="px-2 py-1 bg-black/70 text-white/70 font-mono text-[10px]">Fetching location…</span>
          )}
        </div>
        {coords && (
          <a
            href={`https://www.google.com/maps?q=${coords.lat},${coords.lng}`}
            target="_blank"
            rel="noreferrer"
            className="absolute top-3 right-3 px-2 py-1 bg-[#C9A961] text-neutral-950 font-mono text-[10px] hover:bg-white transition-colors"
            data-testid="geo-map-link"
          >
            Map ↗
          </a>
        )}
      </div>

      {locError && !coords && (
        <div className="border border-[#B33A1F] bg-[#B33A1F]/5 px-3 py-2 font-mono text-sm text-[#B33A1F] flex items-center justify-between">
          <span>{locError}</span>
          <button onClick={getGeo} data-testid="btn-retry-geo" className="underline hover:no-underline">Retry</button>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!snapshot ? (
          <button
            onClick={capture}
            disabled={!streaming}
            data-testid="btn-capture"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-neutral-950 text-[#D4B87D] px-4 py-3 font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors disabled:opacity-50"
          >
            <Camera className="w-4 h-4" /> Capture selfie
          </button>
        ) : (
          <>
            <button
              onClick={retake}
              data-testid="btn-retake"
              className="inline-flex items-center gap-2 border border-[#C9A961] px-4 py-3 font-mono text-sm hover:bg-neutral-950 hover:text-[#D4B87D] transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Retake
            </button>
            <button
              onClick={submit}
              disabled={loading || !coords}
              data-testid="btn-submit-selfie"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-neutral-950 text-[#D4B87D] px-4 py-3 font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
              {loading ? "Uploading…" : "Mark attendance"}
            </button>
          </>
        )}
      </div>

      <div className="text-[11px] font-mono text-neutral-500 leading-relaxed">
        First capture of the day → check-in. Second capture → check-out. Both the photo and your location are stored with the attendance record.
      </div>
    </div>
  );
}
